import { useId, useState, type FormEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { DocumentFields } from '@/features/document-registration/components/DocumentFields';
import {
  EMPTY_DOCUMENT_FORM,
  useDocumentForm,
  type DocumentFormValues,
} from '@/features/document-registration/use-document-form';
import { useActiveCompany } from '@/lib/active-company-context';
import { useCatalog } from '@/lib/quality-catalogs';

import { requestError } from '../presentation';
import type { ApproveInput, DocumentRequest } from '../types';
import { useApproveRequest } from '../use-document-requests';
import { RequestSummary } from './RequestSummary';

// Aprobación de una solicitud de documento nuevo (G3-1; reunión 33:19: Calidad "aprueba
// código y nombre"): el formulario del alta precargado con lo que pidió el solicitante;
// Calidad corrige tipo, proceso, nombre, código o dueño y el documento nace reservado.

function initialValues(r: DocumentRequest): DocumentFormValues {
  return {
    ...EMPTY_DOCUMENT_FORM,
    title: r.proposedTitle,
    documentTypeId: r.documentType.id,
    processAreaId: r.processArea.id,
    owner:
      r.owner === null
        ? null
        : {
            kind: 'qms',
            userId: r.owner.userId,
            name: r.owner.displayName ?? 'Persona sin nombre',
            email: '',
          },
  };
}

export function NewApprovalModal({
  request,
  onClose,
  onReject,
  onDone,
}: {
  request: DocumentRequest;
  onClose: () => void;
  onReject: () => void;
  onDone: (resolved: DocumentRequest) => void;
}): ReactElement {
  const formId = useId();
  const { activeCompanyId } = useActiveCompany();
  const scope = { companyId: activeCompanyId, enabled: true };
  const types = useCatalog('document-types', scope);
  const areas = useCatalog('process-areas', scope);
  const state = useDocumentForm(initialValues(request));
  const approve = useApproveRequest(request.id);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const valid = state.check();
    if (!state.complete || busy || !valid) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { origin: _origin, ownerUserId, ...rest } = await state.payload();
      const input: ApproveInput = { ...rest, ownerUserId: ownerUserId ?? null };
      onDone(await approve.mutateAsync(input));
    } catch (err) {
      const general = state.rejection(err);
      setError(general === null ? null : requestError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      open
      title="Solicitud de documento"
      wide
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn danger sm" onClick={onReject} disabled={busy}>
            Rechazar
          </button>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={busy}>
            Cerrar
          </button>
          <button type="submit" form={formId} className="btn sm" disabled={!state.complete || busy}>
            {busy ? 'Aprobando…' : 'Aprobar y reservar código'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <RequestSummary request={request} showProposal={false} />
      </div>
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        <DocumentFields state={state} types={types} areas={areas} showOrigin={false} />
        {error && (
          <p className="fld-error" role="alert">
            {error}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
